import { createAsyncThunk } from "@reduxjs/toolkit";
import { ThunkApiConfig } from "@shared/models/redux";
import { ProfileAuditResponse } from "@myProfile/models";

/**
 * Fetches profile audit data to check for pending email changes.
 * API: GET /entitlements/v1/user/UserProfile/contact-audit
 *
 * Server resolves the target sfdcContactId from the caller's JWT — no
 * contactId in the URL. Impersonation signalled via entitledUserId, which
 * RequestService appends automatically when impersonating.
 */
export const fetchProfileAuditThunk = createAsyncThunk<ProfileAuditResponse | null, void, ThunkApiConfig>(
  "myProfile/fetchProfileAudit",
  async (_, { extra }) => {
    try {
      const url = "/entitlements/v1/user/UserProfile/contact-audit";
      const response = await extra<ProfileAuditResponse | null>(url, {
        method: "GET",
      });

      // API returns 204 with empty array or null when no audit record exists
      if (!response) {
        return null;
      }

      // Handle case where response exists but data is null/undefined/empty array (204 response)
      if (
        response.data === null ||
        response.data === undefined ||
        (Array.isArray(response.data) && response.data.length === 0)
      ) {
        return null;
      }

      // If data is an array with items, return the first item (audit record)
      const auditData = Array.isArray(response.data) ? response.data[0] : response.data;

      return auditData;
    } catch (error: any) {
      // Handle error responses gracefully
      const status = error?.response?.status || error?.status || error?.statusCode;

      // 204 (No Content) or 404 (Not Found) means no audit record - not an error
      if (status === 204 || status === 404) {
        return null;
      }

      console.error("[Profile Audit API] Error fetching data:", error);

      // Return null instead of rejecting - treat any error as "no audit data"
      // This allows fallback to Snowflake data

      return null;
    }
  }
);
